// src/utils/preloadedData.js

import axios from "axios";
import { API_BASE_URL } from "./config";

export const DEFAULT_PRELOADED = {
    responsible: [],
    operator: [],
    machineCode: [],
    product: [],
};

const STORAGE_PREFIX = "preloaded_properties_cache.v1";
const cacheKey = (userId) => `${STORAGE_PREFIX}:${userId || "anonymous"}`;

export const toUniqueTrimmedList = (values) => {
    const arr = Array.isArray(values) ? values : [];
    const cleaned = arr
        .map((v) => (typeof v === "string" ? v.trim() : ""))
        .filter((v) => !!v);

    const seen = new Set();
    const unique = [];
    for (const item of cleaned) {
        const k = item.toLowerCase();
        if (!seen.has(k)) {
            seen.add(k);
            unique.push(item);
        }
    }
    return unique;
};

export const normalizePreloaded = (input) => {
    const p = input && typeof input === "object" ? input : {};
    return {
        responsible: toUniqueTrimmedList(p.responsible),
        operator: toUniqueTrimmedList(p.operator),
        machineCode: toUniqueTrimmedList(p.machineCode),
        product: toUniqueTrimmedList(p.product),
    };
};

const readCache = (userId) => {
    try {
        const raw = localStorage.getItem(cacheKey(userId));
        if (!raw) return { properties: DEFAULT_PRELOADED, updatedAt: null };
        const parsed = JSON.parse(raw);
        return {
            properties: normalizePreloaded(parsed.properties || parsed),
            updatedAt: parsed.updatedAt || null,
        };
    } catch {
        return { properties: DEFAULT_PRELOADED, updatedAt: null };
    }
};

const writeCache = (userId, payload) => {
    try {
        localStorage.setItem(cacheKey(userId), JSON.stringify(payload));
    } catch {
        // ignore
    }
};

export const fetchPreloadedProperties = async (userId) => {
    if (!userId) return { properties: DEFAULT_PRELOADED, updatedAt: null, exists: false };

    const res = await axios.get(`${API_BASE_URL}configuration/preloaded-properties/${userId}`);
    const properties = normalizePreloaded(res.data?.properties || {});
    const updatedAt = res.data?.updatedAt || null;

    writeCache(userId, { properties, updatedAt });

    return {
        properties,
        updatedAt,
        exists: !!res.data?.exists,
        version: res.data?.version || 0,
    };
};

export const savePreloadedProperties = async (userId, properties) => {
    if (!userId) throw new Error("userId is required");

    const normalized = normalizePreloaded(properties);

    const res = await axios.post(`${API_BASE_URL}configuration/preloaded-properties`, {
        userId,
        properties: normalized,
    });

    const saved = normalizePreloaded(res.data?.properties || {});
    const updatedAt = res.data?.updatedAt || null;

    writeCache(userId, { properties: saved, updatedAt });

    return { properties: saved, updatedAt, version: res.data?.version || 0 };
};

// helper sync (usa cache si existe)
export const getPreloadedOptions = (userId, fieldKey) => {
    const { properties } = readCache(userId);
    if (!fieldKey) return properties;
    return properties[fieldKey] || [];
};
